import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import {
  Plus, Search, Folder, FolderOpen, Users, Calendar,
  FileText, MessageSquare, ChevronRight, Filter
} from 'lucide-react';
import { formatDateFr } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  ouvert:    { label: 'Ouvert',    color: 'bg-emerald-100 text-emerald-700' },
  en_cours:  { label: 'En cours',  color: 'bg-blue-100 text-blue-700' },
  cloture:   { label: 'Clôturé',   color: 'bg-gray-100 text-gray-600' },
  archive:   { label: 'Archivé',   color: 'bg-gray-100 text-gray-400' },
};

const TYPE_CONFIG = {
  contentieux:  { label: 'Contentieux' },
  conseil:      { label: 'Conseil' },
  transaction:  { label: 'Transaction' },
  arbitrage:    { label: 'Arbitrage' },
  autre:        { label: 'Autre' },
};

const DossierCard = ({ dossier, onClick }) => {
  const status = STATUS_CONFIG[dossier.status] || STATUS_CONFIG.ouvert;
  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
            <Folder className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">{dossier.title}</h3>
            {dossier.reference && (
              <p className="text-xs text-gray-400 mt-0.5">{dossier.reference}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.color}`}>
            {status.label}
          </span>
          <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-blue-400 transition-colors" />
        </div>
      </div>

      {dossier.client_name && (
        <p className="text-sm text-gray-600 mb-3 flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5 text-gray-400" />
          {dossier.client_name}
        </p>
      )}

      <div className="flex items-center gap-4 text-xs text-gray-400">
        <span className="flex items-center gap-1">
          <FileText className="h-3.5 w-3.5" />
          {dossier.document_count} doc{dossier.document_count !== 1 ? 's' : ''}
        </span>
        <span className="flex items-center gap-1">
          <MessageSquare className="h-3.5 w-3.5" />
          {dossier.conversation_count} conv.
        </span>
        <span className="flex items-center gap-1 ml-auto">
          <Calendar className="h-3.5 w-3.5" />
          {formatDateFr(dossier.date_ouverture)}
        </span>
      </div>
    </div>
  );
};

const CreateDossierModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    title: '', reference: '', type: 'conseil', status: 'ouvert',
    client_name: '', description: '', hourly_rate_euros: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      const payload = {
        ...form,
        hourly_rate_euros: form.hourly_rate_euros ? Number(form.hourly_rate_euros) : null,
      };
      const { data } = await axios.post(
        `${BACKEND_URL}/api/dossiers`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onCreated(data);
      onClose();
    } catch (err) {
      console.error('Erreur création dossier:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-5">Nouveau dossier</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titre du dossier *
            </label>
            <input
              required
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Ex: Acquisition SAS Dupont — Due Diligence"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Référence
              </label>
              <input
                value={form.reference}
                onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
                placeholder="DOS-2026-001"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="conseil">Conseil</option>
                <option value="contentieux">Contentieux</option>
                <option value="transaction">Transaction</option>
                <option value="arbitrage">Arbitrage</option>
                <option value="autre">Autre</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client
            </label>
            <input
              value={form.client_name}
              onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}
              placeholder="Nom du client ou de l'affaire"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optionnel)
            </label>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Contexte, enjeux, notes..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Taux horaire (€/h)
            </label>
            <input
              type="number"
              min="0"
              step="10"
              value={form.hourly_rate_euros}
              onChange={(e) => setForm((f) => ({ ...f, hourly_rate_euros: e.target.value }))}
              placeholder="Ex : 300 — sinon votre taux par défaut s'applique"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold disabled:opacity-50 transition-colors"
            >
              {loading ? 'Création...' : 'Créer le dossier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Dossiers = ({ user, cabinet, onLogout }) => {
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const fetchDossiers = async () => {
    try {
      const token = localStorage.getItem('lawliet_token');
      const params = {};
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      const { data } = await axios.get(`${BACKEND_URL}/api/dossiers`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setDossiers(data);
    } catch (err) {
      console.error('Erreur chargement dossiers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDossiers(); }, [search, filterStatus]);

  const handleCreated = (newDossier) => {
    setDossiers((prev) => [newDossier, ...prev]);
  };

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-semibold tracking-tight text-gray-900">Dossiers</h1>
            <p className="text-gray-500 text-sm mt-1">
              {dossiers.length} dossier{dossiers.length !== 1 ? 's' : ''} dans votre cabinet
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition-colors"
          >
            <Plus className="h-4 w-4" /> Nouveau dossier
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un dossier, client, référence..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
          >
            <option value="">Tous les statuts</option>
            <option value="ouvert">Ouvert</option>
            <option value="en_cours">En cours</option>
            <option value="cloture">Clôturé</option>
            <option value="archive">Archivé</option>
          </select>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : dossiers.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FolderOpen className="h-8 w-8 text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {search || filterStatus ? 'Aucun dossier correspondant' : 'Aucun dossier pour l\'instant'}
            </h3>
            <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
              {search || filterStatus
                ? 'Essayez de modifier vos filtres de recherche.'
                : 'Créez votre premier dossier pour regrouper documents, contrats et échanges IA par affaire client.'
              }
            </p>
            {!search && !filterStatus && (
              <button
                onClick={() => setShowCreate(true)}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition-colors"
              >
                Créer mon premier dossier
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {dossiers.map((d) => (
              <DossierCard
                key={d.id}
                dossier={d}
                onClick={() => navigate(`/dossiers/${d.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateDossierModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
    </Layout>
  );
};

export default Dossiers;
